// Лента уведомлений: карты, купленные другими, ход, смерть, беспредел, ошибки — очередью, по одному, у правого края.
// Каждое всплывает, держится и уходит вверх с затуханием. Тап по уведомлению убирает его сразу.
//
// Темп подчинён дедлайну: всё, что накопилось, должно уйти за BUDGET_MS с момента последнего добавления, сколько бы
// уведомлений ни было, — время показа каждого сжимается вплоть до MIN_HOLD, а показанное сейчас укорачивается, как
// только очередь выросла. Если и так не успеть, старые «дешёвые» уведомления (чужие карты) выбрасываются и
// заменяются одной сводкой «⏩ пропущено N». Одиночное уведомление держится своё базовое время (карта — секунду).
import { sfx } from './sound.ts';
import type { SoundName } from './sound.ts';

export interface Notice {
  html: string;
  cls?: string;
  /** сколько держать на экране (мс); по умолчанию — по длине текста */
  hold?: number;
  sound?: SoundName;
  /** low — можно выбросить при переполнении (карты, покупки); high — показать обязательно (беспредел, смерть, ошибка) */
  priority?: 'low' | 'high';
  /** тап по уведомлению (кроме того, что убирает его): открыть ящик уведомлений и т.п. */
  onTap?: () => void;
}

const IN_MS = 220;
const OUT_MS = 500;
const MIN_HOLD = 120;
const MAX_HOLD = 4200;
/** за сколько с момента последнего добавления должна уйти вся очередь */
const BUDGET_MS = 3000;
/** базовое время показа: короткое по умолчанию, длинный текст (беспредел, жетон) читается дольше */
const BASE_HOLD = 1000;
/** больше стольких «дешёвых» уведомлений не копим: боты играют быстрее, чем человек читает */
const MAX_LOW = 8;
/** и всего в очереди — не больше стольких (сверх этого выбрасываются и важные, старые первыми) */
const MAX_QUEUE = 14;
/** накладные расходы на одно уведомление (всплытие, уход, пауза) — учитываются при делёжке бюджета */
const OVERHEAD_MS = 140;

const queue: Notice[] = [];
/** момент, к которому очередь должна опустеть */
let deadline = 0;
let showing = false;
let dropped = 0;
let current: { done: () => void; reschedule: () => void } | null = null;

const lane = (): HTMLElement | null => document.getElementById('notify');
const reduce = (): boolean => !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Базовое время показа одного уведомления — по объёму текста */
function baseHold(n: Notice): number {
  if (n.hold != null) return n.hold;
  const text = n.html.replace(/<[^>]+>/g, '');
  return Math.min(MAX_HOLD, Math.max(BASE_HOLD, text.length * 22));
}

/** Время показа с учётом очереди: остаток до дедлайна делится на всех ожидающих, но не больше базового */
function holdFor(n: Notice): number {
  const share = Math.max(0, deadline - Date.now()) / (queue.length + 1) - OVERHEAD_MS;
  return Math.max(MIN_HOLD, Math.min(baseHold(n), share));
}

/** Переполнение: лишние «дешёвые» уведомления выбрасываются, остаётся сводка */
function trim(): void {
  const isSummary = (n: Notice): boolean => !!n.cls?.includes('n-skipped');
  let low = queue.filter((n) => n.priority !== 'high').length;
  for (let i = 0; i < queue.length && low > MAX_LOW; ) {
    const n = queue[i];
    if (n && n.priority !== 'high' && !isSummary(n)) {
      queue.splice(i, 1);
      low--;
      dropped++;
    } else i++;
  }
  for (let i = 0; i < queue.length && queue.length > MAX_QUEUE; ) {
    const n = queue[i];
    if (n && !isSummary(n)) {
      queue.splice(i, 1);
      dropped++;
    } else i++;
  }
  if (dropped) {
    const idx = queue.findIndex((n) => n.cls?.includes('n-skipped'));
    const summary: Notice = { html: `⏩ пропущено уведомлений: ${dropped} — подробности в журнале ☰`, cls: 'n-note n-skipped', hold: 700, priority: 'high' };
    if (idx >= 0) queue[idx] = summary;
    else queue.unshift(summary);
  }
}

export function notify(n: Notice): void {
  queue.push(n);
  // уведомление с явным долгим временем (приглашение в игру) растягивает бюджет очереди под себя
  deadline = Math.max(deadline, Date.now() + Math.max(BUDGET_MS, n.hold ?? 0));
  trim();
  if (!showing) next();
  else current?.reschedule();
}

/** Ленту чистят при смене экрана: показанное не относится к новому */
export function clearNotices(): void {
  queue.length = 0;
  dropped = 0;
  if (current) current.done();
}

function next(): void {
  const n = queue.shift();
  const L = lane();
  if (!n || !L) {
    showing = false;
    if (!queue.length) dropped = 0;
    return;
  }
  showing = true;
  const el = document.createElement('div');
  el.className = `notice ${n.cls ?? ''}`;
  el.innerHTML = n.html;
  L.appendChild(el);
  if (n.sound) sfx(n.sound);
  let finished = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const shownAt = Date.now();
  const done = (): void => {
    if (finished) return;
    finished = true;
    if (timer) clearTimeout(timer);
    current = null;
    const hold = holdFor(n);
    const out = reduce() ? 1 : Math.min(OUT_MS, Math.max(60, hold / 2));
    el.animate([{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(-40px)', opacity: 0 }], { duration: out, easing: 'ease-in', fill: 'forwards' }).onfinish = () => el.remove();
    // следующее начинает всплывать, пока это уходит
    setTimeout(next, Math.min(out, 60));
  };
  /** очередь выросла — пересчитать, сколько ещё держать это уведомление */
  const reschedule = (): void => {
    if (finished) return;
    if (timer) clearTimeout(timer);
    const left = Math.max(0, holdFor(n) + inMs - (Date.now() - shownAt));
    timer = setTimeout(done, left);
  };
  const inMs = reduce() ? 1 : Math.min(IN_MS, holdFor(n));
  current = { done, reschedule };
  el.addEventListener('pointerdown', () => {
    done();
    n.onTap?.();
  });
  el.animate([{ transform: 'translateX(24px)', opacity: 0 }, { transform: 'translateX(0)', opacity: 1 }], { duration: inMs, easing: 'ease-out', fill: 'backwards' });
  reschedule();
}
